import { type FC } from "react";
import { Dialog } from "@mui/material";
import { getLuminance } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  useCreateWidgetMutation,
  type WidgetFormValues,
} from "@/partner/entities/widgets";
import { WidgetForm } from "./WidgetForm";

interface CreateWidgetDialogProps {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}

export const CreateWidgetDialog: FC<CreateWidgetDialogProps> = ({
  open,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [createWidget, { loading }] = useCreateWidgetMutation({
    refetchQueries: ["WidgetsList"],
  });

  const handleSubmit = async (values: WidgetFormValues) => {
    const buttonTextColor =
      getLuminance(values.buttonColor) > 0.5 ? "#000000" : "#ffffff";

    await createWidget({
      variables: { input: { ...values, buttonTextColor } },
      onCompleted: () => {
        enqueueSnackbar(t("widgets.create.success"), { variant: "success" });
        onCompleted();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    });
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <WidgetForm
        title={t("widgets.create.title")}
        loading={loading}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Dialog>
  );
};
